import * as THREE from 'three/webgpu';
import {isSketchContour} from '../../../mesh/meshProxy/SketchContour';
import {isAbstractSketchEdgeObject} from '../../../mesh/sketch';
import {scheduleDisposableResourceDisposal} from '../../../utils';

/**
 * 在当前渲染帧结束后释放草图实体变换副本域拥有的渲染资源。
 *
 * 资源边界说明：
 * 1. 边实体与上色轮廓由 `cloneSelf()` 创建独占 geometry/material，退出副本域时必须释放；
 * 2. 控制点 Sprite 复用点材质工厂资源，不属于副本域，禁止在这里释放；
 * 3. 输入可能同时包含根对象、子对象和已从根树拆下的回滚对象，因此按对象与资源双重去重。
 *
 * @param objects 已退出场景或从未进入场景的变换副本域对象。
 */
export function disposeSketchEntitiesTransformCloneDomainResources(
    objects: Iterable<THREE.Object3D>,
): void {
    const visitedObjects = new Set<THREE.Object3D>();
    const ownedGeometries = new Set<THREE.BufferGeometry>();
    const ownedMaterials = new Set<THREE.Material>();

    /**
     * 收集单个副本对象独占的渲染资源。
     *
     * @param object 当前遍历到的副本域对象。
     */
    const collectOwnedResources = (object: THREE.Object3D): void => {
        if (visitedObjects.has(object)) {
            return;
        }
        visitedObjects.add(object);
        if (!isAbstractSketchEdgeObject(object) && !isSketchContour(object)) {
            return;
        }

        ownedGeometries.add(object.geometry);
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        materials.forEach((material) => ownedMaterials.add(material));
    };

    for (const object of objects) {
        object.traverse(collectOwnedResources);
    }

    // 延迟到当前渲染帧之后，避免 WebGPU/WebGL 仍引用刚离场的顶点或材质缓冲。
    ownedGeometries.forEach(scheduleDisposableResourceDisposal);
    ownedMaterials.forEach(scheduleDisposableResourceDisposal);
}
